import type { SearchFilterApi, SearchRequestSortApi } from '@ingenico/api-sdk-configuration-public';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { Level, showNotification } from '@ingenico/react-notifications';
import { useBusinessService, useBusinessServiceRequest, validateCategoryName } from '@ingenico/react-section-utils';
import type { AutocompleteDeleteEvent } from '@ingenico/react-ui/form-section';
import { useDebouncedState } from '@react-hookz/web';
import { toArray } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { firstValueFrom } from 'rxjs';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';
import type { SmartFieldCategoryOption } from '../domain/models/smartFields.model';
import {
  isNewlyCreatedCategory,
  isValidApiCategory,
  transformCategoryDataToOptions,
  transformMixedValuesToCategoryOptions,
} from '../utils/smartFieldCategories.util';

export function useCreateSmartFieldCategoriesCalls(isCategoryFeatureEnabled?: boolean) {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.SMART_FIELDS]);

  // State for search query
  const [searchQuery, setSearchQuery] = useDebouncedState('', 500);

  // Build search filters
  const searchFilter = useMemo<SearchFilterApi[]>(
    () =>
      searchQuery
        ? [
            {
              filterType: 'fulltext',
              name: '',
              operator: 'equals',
              value: `*${searchQuery}*`,
            },
          ]
        : [],
    [searchQuery],
  );

  // Build request body
  const body = useMemo<{ filters: SearchFilterApi[]; sort: SearchRequestSortApi }>(
    () => ({
      filters: [...searchFilter],
      sort: {},
    }),
    [searchFilter],
  );

  // API call for categories with search
  const {
    isLoading,
    data: categoryData,
    refetch: refetchCategories,
  } = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) =>
        isCategoryFeatureEnabled ? deviceManagementService.searchCategoriesForMap(body) : null,
      [body, isCategoryFeatureEnabled],
    ),
  );

  // Service for creating categories
  const deviceManagementService = useBusinessService(DeviceManagementService);

  // Local state for category creation loading
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  // Transform API data to options format
  const categoryOptions = useMemo(
    (): SmartFieldCategoryOption[] => transformCategoryDataToOptions(toArray(categoryData)),
    [categoryData],
  );

  const handleCategoryChange = useCallback(
    async (
      newValue: (SmartFieldCategoryOption | string)[] | null,
      onChange: (value: SmartFieldCategoryOption[]) => void,
      currentFormValue: SmartFieldCategoryOption[],
    ) => {
      if (newValue === null) {
        return;
      }

      // Transform mixed values to category options
      const transformedValue = transformMixedValuesToCategoryOptions(newValue, categoryOptions);

      // Remove duplicates based on ID
      const uniqueTransformedValue = transformedValue.filter(
        (category, index, array) => array.findIndex((value) => value.id === category.id) === index,
      );

      // Find ONLY the newly added categories by comparing with previous form value
      const newlyAddedCategories = uniqueTransformedValue.filter(
        (newCat) => !currentFormValue.some((existingCat) => existingCat.id === newCat.id),
      );

      // From the newly added, find which ones need API creation (freeSolo entries that don't exist)
      const newCategoriesToCreate = newlyAddedCategories.filter(
        (category) =>
          isNewlyCreatedCategory(category) && !categoryOptions.some((existing) => existing.id === category.id),
      );

      // Validate category names before creation
      const invalidCategories = newCategoriesToCreate.filter((category) => !validateCategoryName(category.label));

      if (invalidCategories.length > 0) {
        showNotification({
          level: Level.Error,
          content: t('category.validation.error', {
            ns: ALL_LNGS_NAMESPACES.SMART_FIELDS,
          }),
        });
        // Don't proceed with creation if there are invalid categories
        return;
      }

      // Separate existing categories from new ones that need to be created
      const existingCategories = uniqueTransformedValue.filter(
        (category) =>
          !isNewlyCreatedCategory(category) || categoryOptions.some((existing) => existing.id === category.id),
      );

      // Update form immediately only with existing categories (not the new ones that need API creation)
      const immediateValue = newCategoriesToCreate.length > 0 ? existingCategories : uniqueTransformedValue;
      onChange(immediateValue);

      // Create new categories if any
      if (newCategoriesToCreate.length > 0) {
        setIsCreatingCategory(true);
        try {
          // Create all new categories in parallel
          const createPromises = newCategoriesToCreate.map((category) =>
            firstValueFrom(deviceManagementService.createCategory({ name: category.label })),
          );

          const createdCategories = await Promise.all(createPromises);

          // Update form with existing categories + newly created categories with real IDs from API
          const newlyCreatedCategoriesWithIds = createdCategories
            .filter((created) => isValidApiCategory(created))
            .map((created) => ({ id: created.id, label: created.name }));

          const finalValue = [...existingCategories, ...newlyCreatedCategoriesWithIds];
          onChange(finalValue);

          // Clear search and refetch categories
          setSearchQuery('');
          refetchCategories();
        } catch {
          showNotification({
            level: Level.Error,
            content: t('category.created.error'),
          });
        } finally {
          setIsCreatingCategory(false);
        }
      }
    },
    [categoryOptions, t, refetchCategories, deviceManagementService, setSearchQuery],
  );

  const handleCategoryDelete = useCallback(
    (
      event: AutocompleteDeleteEvent,
      categories: SmartFieldCategoryOption[],
      setCategories: (value: SmartFieldCategoryOption[]) => void,
    ) => {
      const indexToBeRemoved = event.index;
      const newValue = categories.filter((_, index: number) => index !== indexToBeRemoved);
      setCategories(newValue);
    },
    [],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
    },
    [setSearchQuery],
  );

  const noOptionsText = useMemo(() => {
    return t('noOptions');
  }, [t]);

  return {
    handleCategoryChange,
    handleCategoryDelete,
    categoryOptions,
    isCreatingCategory,
    isLoading,
    searchQuery,
    handleSearchChange,
    noOptionsText,
    setSearchQuery,
  };
}
